import React, { useState } from "react";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "reactstrap";
import { X } from "lucide-react";
import styled from "styled-components";
import usePaging from "../../hooks/usePaging";
import useFetcher from "../../hooks/useFetcher";
import { fetchTicketingCategories, fetchTickets, fetchPiAsignable } from "../../services/ticketingServices";
import { CustomLabel } from "../../ui/labels";
import SelectV2 from "../SelectV2";
import { ButtonSecondaryV2, SpinnerButtonPrimary } from "../../ui/buttons";

const FormContainer = styled.div`
  width: 100%;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  font-family: "Nunito", sans-serif;
`;

const BehalfModal = ({ formik, toggle, setisOnBehalf }) => {
  const pagingController = usePaging(fetchTickets);
  const [selectedPI, setSelectedPI] = useState(false);
  const [piAsignable, isLoadingPiAsignable] = useFetcher(fetchPiAsignable, undefined, "allfetchPiAsignable");
  const [ticketingCategories, isLoadingTicketingCategories] = useFetcher(fetchTicketingCategories, undefined, "fetchTicketingCategories");

  const handleReactSelectChange = (selected, options) => {
    const newState = { [options.name]: selected ? selected.value : null };
    if (newState.region) {
      newState.sucursal = null;
    }
    if ((newState.categoria && newState.categoria === 4) || newState.piAsignado) {
      setSelectedPI(true);
    } else {
      setSelectedPI(false);
    }
    pagingController.setFilterValue(newState);
  };

  const piAsignableOptions = piAsignable
    .map((pi) => {
      return { label: pi.nombreApellido, value: pi.itemId };
    })
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  const categoriesOptions = ticketingCategories.categoria
    ? ticketingCategories.categoria
        .map((categoria) => {
          return { label: categoria.text, value: categoria.id };
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1))
    : [];

  return (
    <Modal isOpen toggle={toggle} style={{ fontFamily: "Nunito" }}>
      <ModalHeader style={{ borderBottom: "none", width: "100%" }}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <p className="mb-0 w-100 text-center" style={{ fontSize: "20px", fontWeight: "700", marginRight: "50px" }}>
            Crear ticket a nombre de otra persona
          </p>
          <X
            size={30}
            className="cursor-pointer"
            onClick={() => {
              toggle();
              setisOnBehalf(false);
              formik.resetForm();
            }}
          />
        </div>
      </ModalHeader>
      <ModalBody>
        <FormContainer>
          <div className="w-100">
            <CustomLabel htmlFor="categoria" fontSize="18px" fontWeight="800">
              Público
              <SelectV2
                height="38px"
                id="categoria"
                name="categoria"
                // formik={formik}
                onChange={(event) => {
                  handleReactSelectChange(event, { name: "categoria" });
                  formik.setFieldValue("categoria", event ? event.value : null);
                }}
                value={categoriesOptions.find((option) => {
                  return option.value === parseInt(pagingController.query.categoria, 10);
                })}
                placeholder="Todo"
                isClearable
                isLoading={isLoadingTicketingCategories}
                disabled={isLoadingTicketingCategories}
                options={categoriesOptions}
              />
            </CustomLabel>
            {formik.errors.categoria && formik.touched.categoria && (
              <CustomLabel fontSize="12px" color="red">
                {formik.errors.categoria}
              </CustomLabel>
            )}
          </div>
          {selectedPI ? (
            <div className="w-100">
              <CustomLabel htmlFor="piAsignado" fontSize="18px" fontWeight="800">
                Para:
                <SelectV2
                  height="38px"
                  id="piAsignado"
                  name="piAsignado"
                  // formik={formik}
                  onChange={(event) => {
                    handleReactSelectChange(event, { name: "piAsignado" });
                    formik.setFieldValue("piAsignado", event ? event.value : null);
                  }}
                  value={piAsignableOptions.find((option) => {
                    return option.value === parseInt(pagingController.query.piAsignado, 10);
                  })}
                  placeholder="Todos"
                  isClearable
                  required
                  isLoading={isLoadingPiAsignable}
                  disabled={isLoadingPiAsignable}
                  options={piAsignableOptions}
                />
              </CustomLabel>
              {formik.errors.piAsignado && formik.touched.piAsignado && (
                <CustomLabel fontSize="12px" color="red">
                  {formik.errors.piAsignado}
                </CustomLabel>
              )}
            </div>
          ) : (
            <>
              <div className="w-100">
                <CustomLabel htmlFor="nombre" fontSize="18px" fontWeight="800">
                  Nombre
                </CustomLabel>
                <input
                  className="form-control"
                  type="string"
                  placeholder="Nombre"
                  name="nombre"
                  required
                  disabled={formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.nombre}
                />
                {formik.errors.nombre && formik.touched.nombre && (
                  <CustomLabel fontSize="12px" color="red">
                    {formik.errors.nombre}
                  </CustomLabel>
                )}
              </div>
              <div className="w-100">
                <CustomLabel htmlFor="apellido" fontSize="18px" fontWeight="800">
                  Apellido
                </CustomLabel>
                <input
                  className="form-control"
                  type="string"
                  placeholder="Apellido"
                  name="apellido"
                  required
                  disabled={formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.apellido}
                />
                {formik.errors.apellido && formik.touched.apellido && (
                  <CustomLabel fontSize="12px" color="red">
                    {formik.errors.apellido}
                  </CustomLabel>
                )}
              </div>
              <div className="w-100">
                <CustomLabel htmlFor="dniCreador" fontSize="18px" fontWeight="800">
                  DNI
                </CustomLabel>
                <input
                  className="form-control"
                  type="number"
                  placeholder="Numero de documento"
                  name="dniCreador"
                  required={formik.values.categoria === "1"}
                  disabled={formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.dniCreador}
                />
                {formik.errors.dniCreador && formik.touched.dniCreador && <div className="input-feedback">{formik.errors.dniCreador}</div>}
              </div>
              <div className="w-100">
                <CustomLabel htmlFor="email" fontSize="18px" fontWeight="800">
                  Email
                </CustomLabel>
                <input
                  className="form-control"
                  type="email"
                  placeholder="Email"
                  name="email"
                  required
                  disabled={formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                {formik.errors.email && formik.touched.email && (
                  <CustomLabel fontSize="12px" color="red">
                    {formik.errors.email}
                  </CustomLabel>
                )}
              </div>
              <div className="w-100">
                <CustomLabel htmlFor="telefonoCreador" fontSize="18px" fontWeight="800">
                  Telefono
                </CustomLabel>
                <input
                  className="form-control"
                  type="number"
                  placeholder="Telefono"
                  name="telefonoCreador"
                  disabled={formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.telefonoCreador}
                />
              </div>
            </>
          )}
        </FormContainer>
      </ModalBody>
      <ModalFooter style={{ borderTop: "none", width: "100%" }}>
        <div className="d-flex justify-content-between align-items-center w-100" style={{ padding: "0px 10px" }}>
          <ButtonSecondaryV2
            justifyContent="center"
            padding="10px"
            width="100%"
            isLoading={false}
            disabled={false}
            onClick={() => {
              formik.resetForm();
              setisOnBehalf(false);
              toggle();
            }}
          >
            Cancelar
          </ButtonSecondaryV2>
          <SpinnerButtonPrimary padding="10px" justifyContent="center" marginRight="0px" width="100%" isLoading={false} disabled={false} onClick={() => toggle()}>
            Guardar
          </SpinnerButtonPrimary>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default BehalfModal;
